import { memo, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { IconButton } from '../button/button';
// Default SVG import resolves to a URL string under current Storybook webpack config.
// Use it as an <img src> instead of attempting to render as a React component.
import clsx from 'clsx';
import avatarUrl from '../../assets/icons/avatar.svg';

export type ProfileNavItem = {
  name: string;
  to?: string;
  action?: () => void;
  authorized?: boolean;
};

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  hasAdminAccess: boolean;
  fullName?: string;
};

export type ProfileProps = {
  user?: User;
  imageSrc?: string;
  items: ProfileNavItem[];
  collapsedMode?: boolean;
  onClick?: (state: boolean) => void;
};

const Profile = memo(({ user, items, imageSrc, collapsedMode, onClick }: ProfileProps) => {
  const [isOpen, setOpen] = useState(false);
  const contentRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    collapsedMode && setOpen(false);
  }, [collapsedMode]);

  const onClickHandler = () => {
    onClick && onClick(false);
    collapsedMode && setOpen(true);
  };

  const resolveUserValue = (user: User) => {
    if (user.fullName) {
      return user.fullName;
    } else {
      return `${user.firstName} ${user.lastName}`;
    }
  };

  const containerStyles = clsx('odin-transition-colors odin-ease odin-duration-300', {
    'odin-bg-gray-450 dark:odin-bg-gray-3': isOpen,
  });
  const avatarStyles = clsx('odin-mr-2', { 'odin-cursor-pointer': collapsedMode });
  const profileMenuStyles = { height: `${isOpen ? contentRef.current?.offsetHeight : 0}px` };
  const angleButtonStyles = clsx('odin-transition-transform odin-duration-100 odin-px-2 odin-py-2 odin-ml-auto', {
    'odin-rotate-180': !isOpen,
  });

  return (
    <div className={containerStyles}>
      <div className="odin-flex odin-items-center odin-py-2 odin-pl-3">
        <div className={avatarStyles} onClick={() => onClickHandler()}>
          {imageSrc ? (
            <img src={imageSrc} width={40} alt="profile image" className="odin-rounded-full" />
          ) : (
            <img src={avatarUrl} width={40} alt="profile image" className="odin-rounded-full" />
          )}
        </div>
        {!collapsedMode && (
          <>
            <div className="odin-overflow-hidden odin-text-ellipsis odin-text-sm">{user && resolveUserValue(user)}</div>
            <IconButton classNames={angleButtonStyles} onClick={() => setOpen(!isOpen)} icon="ChevronUp" />
          </>
        )}
      </div>

      <div className="odin-ease odin-overflow-hidden odin-transition-all" style={profileMenuStyles}>
        <ul ref={contentRef}>
          {items.map((item) => {
            if (item.authorized !== false) {
              return (
                <li
                  key={item.name}
                  className="odin-flex odin-h-9 odin-w-full odin-items-center odin-text-sm odin-transition-colors odin-duration-300 hover:odin-bg-primary-4 dark:odin-hover:bg-gray-4"
                  onClick={() => setOpen(false)}
                >
                  {item.to ? (
                    <NavLink className="odin-flex odin-w-full odin-px-5" to={item.to}>
                      {item.name}
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      className="odin-flex odin-w-full odin-px-5 odin-bg-transparent odin-border-none odin-cursor-pointer odin-text-inherit"
                      onClick={item.action}
                    >
                      {item.name}
                    </button>
                  )}
                </li>
              );
            }
          })}
        </ul>
      </div>
    </div>
  );
});

Profile.displayName = 'User';
export default Profile;
